import { useEffect, useState, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { cotizar as cotizarLocal } from '@/lib/cd2bPricing'
import { usePaises, vendibles } from '@/lib/pais/usePaises'
import { mensajeDeError } from '@/lib/errores'
import { useAuthStore } from '@/store/authStore'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { resolverAlcance, edicionCubre, impuestoDelPais } from './paquete'
import {
  modalidadEntregaCatalogo,
  frenoCatalogoEnCreacion,
  declaracionAEnviar,
  estadoFrenoCatalogo,
} from '@/lib/catalogoContadorModalidad'

const CATALOGO_VACIO = { ediciones: [], modulos: [], seatsPorEdicion: {}, areas: [], areaMeta: {}, giros: [] }

/**
 * TODO el estado y las llamadas del editor de cotizaciones, sin una sola línea de presentación.
 *
 * Antes vivía en un componente de 1.800 líneas que pintaba ocho tarjetas seguidas. Al partirlo en
 * cuatro pasos, la regla fue que ningún paso tuviera estado propio que importe: el formulario, el
 * precio en vivo, la comisión de la política del país y los frenos («sin país no hay demo») se
 * calculan aquí UNA vez, y cada paso solo lee y llama. Así saltar de un paso a otro no pierde nada
 * y el resumen fijo dice siempre lo mismo que el paso que se está editando.
 *
 * Las confirmaciones (`window.confirm`/`prompt` en la versión anterior) pasan por
 * `useConfirmacion`: el orquestador pinta `dialogo` una vez.
 */
export function useCotizacion(id) {
  const esNueva = !id || id === 'nueva'
  const { confirmar, pedirDatos, dialogo } = useConfirmacion()

  const [catalogo, setCatalogo] = useState(CATALOGO_VACIO)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Propuesta cargada (para conocer estado/demo) + datos de acceso de la empresa demo.
  const [propuesta, setPropuesta] = useState(null)
  // Si el cliente trae el catálogo de su contador, «Crear empresa» espera a que se entienda.
  // Arranca en `propio: false` mientras no se sepa: el freno no puede activarse por no
  // haber podido preguntar.
  const [catalogoContador, setCatalogoContador] = useState({ propio: false, listo: false, faltan: [] })
  // La respuesta sobre el catálogo del contador mientras la cotización todavía NO EXISTE: no
  // hay dónde guardarla, así que espera aquí y viaja con el primer guardado.
  const [declaracionCatalogo, setDeclaracionCatalogo] = useState(null)
  const [demoInfo, setDemoInfo] = useState(null)
  // Países habilitados (GET /v1/public/cd2b/paises). `null` = cargando; `error` si falló.
  // Solo se ofrecen los que NO están en hoja de ruta. Sin lista de respaldo (anti-fallback).
  const paisesApi = usePaises()
  const paises = paisesApi.cargando ? null : vendibles(paisesApi.paises)
  const paisesError = Boolean(paisesApi.error)
  // Cargas de datos del tenant (de ejemplo e importadas). La decisión de entrega vive en
  // `DatosAlEntregar`, que la lee y la guarda por su cuenta.
  const [cargas, setCargas] = useState(null)
  const [cargasCargando, setCargasCargando] = useState(false)
  // Profundidad de la siembra. 'catalogo' = 5-6 productos planos (segundos); 'operacion' =
  // la empresa demo completa del giro con ~12 meses de historia (minutos). Hasta ahora la
  // pantalla pedía SIEMPRE 'catalogo', así que el motor rico —que existe y está cableado
  // de punta a punta— no se podía alcanzar desde el portal.
  const [alcanceSiembra, setAlcanceSiembra] = useState('operacion')
  const [sembrando, setSembrando] = useState(false)
  // Carga que se está revirtiendo, con su vista previa de conteos.
  const [reversion, setReversion] = useState(null)
  // Accesos de los puestos sembrados (usuario + PIN), para entregárselos al prospecto.
  const [credenciales, setCredenciales] = useState(null)
  // Chequeo previo a sembrar: qué pide la plantilla del giro contra lo que se cotizó.
  const [chequeoSiembra, setChequeoSiembra] = useState(null)
  // Vaciado previo: lo que hay ahora en la empresa demo y se perdería al limpiarla.
  const [vaciado, setVaciado] = useState(null)
  // Lista de verificación previa a la puesta en producción.
  const [chequeo, setChequeo] = useState(null)

  const [form, setForm] = useState({
    clienteEmail: '',
    empresaNombre: '',
    // País (ISO-2) de la empresa. Obligatorio para crear la empresa; sin valor por defecto.
    pais: '',
    giro: '',
    edicion: 'pyme',
    modulosSel: [],
    seats: { completo: 0, operativo: 0 },
    capacidad: [],
    cargosAdicionales: [],
    descuento: null,
    precioMensual: 0,
    moneda: 'USD',
    // Cómo paga el cliente: 'mensual' (cada mes) o 'anual' (un año por adelantado, con el descuento anual).
    ciclo: 'mensual',
    modalidad: 'local',
    instanciaNube: { host: '', tenantId: '' },
    // Identidad fiscal de la empresa REAL. `null` = el asesor todavía no ha tocado el bloque;
    // se distingue de «capturada y vacía» para no mandar un emisor en blanco al guardar.
    emisor: null,
    notas: '',
  })

  // Catálogos del MH (actividad económica + geografía). Van aparte del catálogo comercial:
  // son públicos, inmutables y cacheados 24 h.
  const [catFiscal, setCatFiscal] = useState(null)
  const [catFiscalCargando, setCatFiscalCargando] = useState(true)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const cat = await cd2bPropuestaService.getCatalogo()
      const catData = cat?.data || CATALOGO_VACIO
      setCatalogo(catData)
      if (esNueva) {
        // Una cotización nueva arranca con TODO lo que trae el plan marcado (y su cupo de asientos):
        // el precio de la edición ya los incluye, así que desmarcar es un RECORTE, no un ahorro.
        setForm((f) => {
          const ed = (catData.ediciones || []).find((e) => e.edicion === f.edicion)
          return {
            ...f,
            modulosSel: ed?.modulosIncluidos || [],
            seats: {
              completo: f.seats.completo || Number(ed?.seatsIncluidos?.completo) || 0,
              operativo: f.seats.operativo || Number(ed?.seatsIncluidos?.operativo) || 0,
            },
          }
        })
      }
      if (!esNueva) {
        const res = await cd2bPropuestaService.getById(id)
        const p = res?.data
        if (p) {
          setPropuesta(p)
          setForm((f) => ({
            ...f,
            clienteEmail: p.userId?.email || '',
            empresaNombre: p.empresaNombre || '',
            pais: p.pais || '',
            giro: p.giro || '',
            edicion: p.edicion || 'pyme',
            modulosSel: (p.modulos || []).map((m) => m.code),
            seats: { completo: p.seats?.completo || 0, operativo: p.seats?.operativo || 0 },
            capacidad: (p.capacidad || []).map((c) => ({ code: c.code, cantidad: c.cantidad })),
            cargosAdicionales: p.cargosAdicionales || [],
            descuento: p.descuento ? { tipo: p.descuento.tipo, valor: p.descuento.valor, motivo: p.descuento.motivo || '', mesesPromo: p.descuento.mesesPromo || '' } : null,
            precioMensual: p.precioMensual || 0,
            moneda: p.moneda || 'USD',
            ciclo: p.ciclo === 'anual' ? 'anual' : 'mensual',
            modalidad: p.modalidad || 'local',
            instanciaNube: { host: p.instanciaNube?.host || '', tenantId: p.instanciaNube?.tenantId || '' },
            emisor: p.emisor || null,
            notas: p.notas || '',
          }))
          // Ya pagada: mostrar de una los requisitos que faltan para arrancar, sin que el asesor
          // tenga que pulsar nada para enterarse.
          if (p.estado === 'pagada') {
            cd2bPropuestaService.listoParaProduccion(p._id)
              .then((r) => setChequeo(r?.data || null))
              .catch(() => { /* se puede revisar a mano con el botón */ })
          }
        }
      }
    } catch (e) {
      toast.error(mensajeDeError(e, 'Error al cargar'))
    } finally {
      setLoading(false)
    }
  }, [id, esNueva])

  useEffect(() => { cargar() }, [cargar])

  /**
   * Vuelve a leer SOLO la propuesta, sin tocar el formulario ni encender `loading`.
   *
   * `cargar` pone la pantalla entera en «cargando» (el editor se desmonta) y reescribe el
   * formulario: para refrescar el cobro —el proveedor elegido, los comprobantes que sube el
   * cliente— eso borraría lo que la tarjeta tiene a la vista. Devuelve la propuesta leída, o
   * `null` si no se pudo (quien llama decide si avisa).
   */
  const recargarPropuesta = useCallback(async () => {
    if (esNueva) return null
    try {
      const p = (await cd2bPropuestaService.getById(id))?.data || null
      if (p) setPropuesta(p)
      return p
    } catch {
      return null
    }
  }, [id, esNueva])

  // Catálogos fiscales: petición propia, best-effort. Si fallan, el bloque de identidad fiscal
  // sale con los desplegables vacíos y lo dice — no se inventa un catálogo local de respaldo,
  // que es exactamente cómo se cuelan los códigos que Hacienda rechaza.
  useEffect(() => {
    let vivo = true
    cd2bPropuestaService.getCatalogosFiscales()
      .then((r) => { if (vivo) setCatFiscal(r?.data || null) })
      .catch(() => { if (vivo) setCatFiscal(null) })
      .finally(() => { if (vivo) setCatFiscalCargando(false) })
    return () => { vivo = false }
  }, [])

  // Precio EN VIVO con el espejo del motor (idéntico al backend). Se recalcula al marcar módulos,
  // cambiar edición, asientos o cargos. La autoridad al guardar es el backend (/cotizar).
  const quote = useMemo(
    () => cotizarLocal(
      { edicion: form.edicion, modulos: form.modulosSel, seats: form.seats, capacidad: form.capacidad, cargos: form.cargosAdicionales, descuento: form.descuento, moneda: form.moneda },
      catalogo,
    ),
    [form.edicion, form.modulosSel, form.seats, form.capacidad, form.cargosAdicionales, form.descuento, form.moneda, catalogo],
  )

  // El precio SIEMPRE sigue al total del motor: no hay override manual que pueda congelarlo.
  useEffect(() => {
    setForm((f) => (Number(f.precioMensual) === quote.totalMensual ? f : { ...f, precioMensual: quote.totalMensual }))
  }, [quote.totalMensual])

  // Vista previa de la cotización (documento que se le envía al dueño).
  const [previewOpen, setPreviewOpen] = useState(false)
  const usuario = useAuthStore((s) => s.user)
  const asesorNombre = [usuario?.firstName, usuario?.lastName].filter(Boolean).join(' ') || usuario?.name || ''
  const previewData = useMemo(() => ({
    tipo: 'cotizacion',
    empresa: form.empresaNombre || '',
    edicionLabel: (catalogo.ediciones || []).find((e) => e.edicion === form.edicion)?.label || form.edicion,
    modulosActivos: (catalogo.modulos || []).filter((m) => (form.modulosSel || []).includes(m.code)).map((m) => m.label),
    desglose: quote.desglose || [],
    cargosUnicos: quote.cargosUnicos || [],
    totalMensual: quote.totalMensual || 0,
    totalUnico: quote.totalUnico || 0,
    descuento: quote.descuento || null,
    moneda: form.moneda,
    seats: { completo: Number(form.seats?.completo) || 0, operativo: Number(form.seats?.operativo) || 0 },
    asesor: asesorNombre,
  }), [form.empresaNombre, form.edicion, form.modulosSel, form.moneda, form.seats, quote, catalogo, asesorNombre])

  // Comisión del asesor: la calcula el backend con la POLÍTICA DEL PAÍS de la cotización (docs/179),
  // la misma con la que después se paga. Sin país, sin tipo de asesor o sin política llega el
  // motivo, no un número. Debounced para no pegarle por cada tecla. También en cotizaciones nuevas.
  const [comision, setComision] = useState(null)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await cd2bPropuestaService.cotizar({
          edicion: form.edicion, modulos: form.modulosSel.map((code) => ({ code })),
          seats: form.seats, capacidad: form.capacidad, cargos: form.cargosAdicionales, descuento: form.descuento, moneda: form.moneda,
          pais: form.pais || undefined,
        })
        if (res?.data) setComision({ comisionMensual: res.data.comisionMensual, totalMensual: res.data.totalMensual, detalle: res.data.comision || null, anual: res.data.anual || null })
      } catch { /* el espejo local ya muestra el total */ }
    }, 500)
    return () => clearTimeout(t)
  }, [form.edicion, form.modulosSel, form.seats, form.capacidad, form.cargosAdicionales, form.descuento, form.moneda, form.pais])

  const toggleModulo = (code) => {
    setForm((f) => ({
      ...f,
      modulosSel: f.modulosSel.includes(code)
        ? f.modulosSel.filter((c) => c !== code)
        : [...f.modulosSel, code],
    }))
  }

  // Complementos RECURRENTES (soporte, negocio adicional): se marcan como los módulos, pero suman
  // un cargo mensual (`cargosAdicionales`) en vez de un área. Siguen la misma lógica de check.
  const cargoActivo = (code) => form.cargosAdicionales.some((c) => c.code === code)

  // ¿El paquete incluye la app del teléfono? Decide si tiene sentido entregarle al dueño el
  // código de empresa: sin el canal vendido, la app lo rechaza en la puerta (402) y el asesor
  // se entera por el cliente. El código sale del catálogo, no escrito a mano.
  const appMovilVendida = (catalogo.addons?.acceso || []).some((a) => cargoActivo(a.code))
  const toggleAddonCargo = (a) => setForm((f) => ({
    ...f,
    cargosAdicionales: f.cargosAdicionales.some((c) => c.code === a.code)
      ? f.cargosAdicionales.filter((c) => c.code !== a.code)
      : [...f.cargosAdicionales, { code: a.code, name: a.label, amount: a.precio, frequency: 'monthly', origen: 'addon' }],
  }))

  /** Códigos de los módulos que la edición `ed` cubre por completo (los que "trae el plan"). */
  const modulosDeEdicion = useCallback((ed) => {
    const areas = (catalogo.ediciones || []).find((e) => e.edicion === ed)?.areas || []
    return (catalogo.modulos || []).filter((m) => edicionCubre(areas, m)).map((m) => m.code)
  }, [catalogo])

  /**
   * Cambio de GIRO. Solo fija el giro; la edición sugerida de la plantilla se OFRECE aparte
   * (el asesor puede haber acordado otra) para no pisar un paquete ya armado.
   */
  const elegirGiro = (giro) => setForm((f) => ({ ...f, giro }))

  // Vista previa del MENÚ real (entradas, no áreas). El asesor prometía «Inventario» y el
  // cliente contaba pantallas: nadie sabía si un `micro` abriría el ERP con 9 o con 26.
  const [menuPrevio, setMenuPrevio] = useState(null)
  useEffect(() => {
    let vivo = true
    if (!form.edicion) return undefined
    cd2bPropuestaService
      .previsualizarMenu({ edicion: form.edicion, giro: form.giro, modulos: form.modulosSel })
      .then((r) => { if (vivo) setMenuPrevio(r?.data || null) })
      .catch(() => { if (vivo) setMenuPrevio(null) })
    return () => { vivo = false }
  }, [form.edicion, form.giro, form.modulosSel])

  /**
   * Cambio de EDICIÓN. Parte de "todo lo que trae el plan" marcado: es lo que el cliente
   * contrata por defecto, y el asesor DESMARCA lo que no va a implementar. Antes había que
   * marcar módulo por módulo sin saber cuáles traía la edición, y marcar dos no restringía nada.
   * Además rellena los asientos incluidos si aún están en 0 (no pisa lo que el asesor escribió).
   */
  const cambiarEdicion = (ed) => {
    const incluidos = modulosDeEdicion(ed)
    const seatsBase = catalogo.seatsPorEdicion?.[ed] || {}
    // Verticales/add-on que NO trae ningún plan (Crédito, Cobranza, Territorio, Inventario avanzado):
    // se venden aparte, así que cambiar de edición no puede borrarlos del paquete.
    const esVertical = (c) => !((catalogo.modulos || []).find((m) => m.code === c)?.incluidoEn || []).length
    // Emprendedor es gratis y nunca pasa por Stripe: el cliente no tiene tarjeta registrada, así
    // que cambiar a esa edición desmarca solo cualquier add-on de pago que quedara seleccionado.
    const permiteExtras = (catalogo.ediciones || []).find((e) => e.edicion === ed)?.permiteExtrasPago !== false
    setForm((f) => ({
      ...f,
      edicion: ed,
      // Marca todo lo que trae el plan nuevo y conserva las verticales ya vendidas.
      modulosSel: [...new Set([...incluidos, ...f.modulosSel.filter(esVertical)])],
      seats: {
        completo: f.seats.completo || Number(seatsBase.completo) || 0,
        operativo: f.seats.operativo || Number(seatsBase.operativo) || 0,
      },
      cargosAdicionales: permiteExtras ? f.cargosAdicionales : f.cargosAdicionales.filter((c) => c.origen !== 'addon'),
      capacidad: permiteExtras ? f.capacidad : [],
    }))
  }

  /** Marca los módulos que el chequeo de siembra echó en falta. Guardar sigue siendo explícito. */
  const sumarModulos = (codigos) => setForm((f) => ({ ...f, modulosSel: [...new Set([...(f.modulosSel || []), ...codigos])] }))

  /**
   * Guarda (crea o actualiza). Devuelve el id de la cotización guardada, o null si falló: el
   * orquestador decide a qué paso llevar al asesor después.
   */
  const guardar = async () => {
    setSaving(true)
    try {
      const payload = {
        clienteEmail: form.clienteEmail,
        empresaNombre: form.empresaNombre,
        // `undefined` (no `''`) si no se eligió: el borrador puede guardarse sin país.
        pais: form.pais || undefined,
        giro: form.giro || undefined,
        edicion: form.edicion,
        modulos: form.modulosSel.map((code) => ({ code })),
        seats: form.seats,
        capacidad: form.capacidad,
        cargosAdicionales: form.cargosAdicionales,
        descuento: form.descuento && Number(form.descuento.valor) > 0 ? form.descuento : null,
        // El precio no se manda: el backend SIEMPRE lo deriva del paquete con el motor.
        moneda: form.moneda,
        ciclo: form.ciclo === 'anual' ? 'anual' : 'mensual',
        modalidad: form.modalidad,
        instanciaNube: form.modalidad === 'nube' ? form.instanciaNube : undefined,
        // `undefined` (no `null`) cuando no se ha tocado: `null` significa BORRAR la captura.
        emisor: form.emisor || undefined,
        notas: form.notas,
      }
      if (esNueva) {
        const res = await cd2bPropuestaService.create(payload)
        const nuevoId = res?.data?._id
        // La respuesta sobre el catálogo del contador se contestó antes de que existiera la
        // cotización. Va en cuanto existe, y en su propia llamada porque `create` no la acepta:
        // vive en `catalogoContador`, que tiene su propio endpoint para no pisar el archivo ya
        // subido con una fusión superficial.
        //
        // Solo en modalidad nube (B1): en local/vps el catálogo se trae desde el escritorio, y
        // una declaración que quedó en el estado de antes de cambiar la modalidad no se envía.
        const declaracion = declaracionAEnviar({ modalidad: form.modalidad, declaracion: declaracionCatalogo })
        if (nuevoId && declaracion) {
          try {
            await cd2bPropuestaService.declararCatalogoContador(nuevoId, declaracion)
            // Y el archivo que el asesor eligió con el cliente delante, si lo eligió. Se sube
            // aquí y no antes porque el catálogo se guarda EN la cotización.
            if (declaracion.archivo) {
              const r = await cd2bPropuestaService.analizarCatalogoContador(nuevoId, {
                archivo: declaracion.archivo,
              })
              toast.success(`Se leyeron ${r?.data?.cuentas ?? 0} cuentas de «${r?.data?.archivo || 'su catálogo'}».`)
            }
          } catch (e) {
            // No se pierde en silencio: sin esto, «Crear empresa» va a frenar más tarde y el
            // asesor no sabría por qué. Se dice ahora, con la cotización ya abierta delante.
            toast(
              `La cotización se creó, pero el catálogo del contador no se guardó del todo (${
                mensajeDeError(e, 'error')
              }). Terminalo en «Revisar y enviar».`,
              { icon: '⚠️', duration: 8000 },
            )
          }
        }
        toast.success('Cotización creada')
        return nuevoId || null
      }
      await cd2bPropuestaService.update(id, payload)
      toast.success('Cotización actualizada')
      return id
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo guardar'))
      return null
    } finally {
      setSaving(false)
    }
  }

  // Envía la cotización al dueño por correo con el link público de aceptación.
  const [enviando, setEnviando] = useState(false)
  const enviarAlDueno = async () => {
    setEnviando(true)
    try {
      const r = await cd2bPropuestaService.enviarAlDueno(id, { incluirAccesoDemo: true })
      toast.success(r?.data?.conDemo
        ? 'Cotización enviada. El correo le anuncia su empresa de prueba; las credenciales las verá en el enlace.'
        : 'Cotización enviada al dueño por correo')
      cargar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo enviar la cotización'))
    } finally {
      setEnviando(false)
    }
  }

  // Crea la empresa demo (tenant nube real) para que el DUEÑO la pruebe por la app.
  const crearDemo = async () => {
    try {
      const res = await cd2bPropuestaService.crearDemo(id)
      const d = res?.data || {}
      setDemoInfo(d)
      if (d.negocioCodigo) { try { await navigator.clipboard.writeText(d.negocioCodigo) } catch { /* noop */ } }
      toast.success('Empresa demo creada — código copiado')
      // El catálogo del contador entra al crear la empresa: si no entró entero o quedó abierto,
      // el asesor tiene que enterarse AHORA, no al intentar entregarla.
      const cat = d.catalogoCuentas
      if (cat && cat.ok === false) {
        toast.error(`El catálogo del contador no entró entero: ${cat.error || cat.motivo || 'sin detalle'}. Revísalo en «Catálogo de cuentas».`, { duration: 8000 })
      } else if (cat && !cat.catalogoCerrado) {
        toast(`El catálogo del contador entró, pero quedó abierto: ${cat.avisoCierre || 'falta la firma del contador.'}`, { icon: '⚠️', duration: 8000 })
      }
      cargar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo crear la empresa demo'))
    }
  }

  // Reobtiene el código de empresa + PIN del dueño (por si el asesor lo perdió).
  const verAcceso = async () => {
    try {
      const res = await cd2bPropuestaService.accesoDemo(id)
      const d = res?.data || {}
      setDemoInfo(d)
      cargar()
      if (d.negocioCodigo) { try { await navigator.clipboard.writeText(d.negocioCodigo) } catch { /* noop */ } }
      toast.success('Acceso del dueño — código copiado')
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo obtener el acceso'))
    }
  }

  const extenderDemo = async () => {
    try {
      await cd2bPropuestaService.extenderDemo(id, 15)
      toast.success('Prueba extendida 15 días')
      cargar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo extender'))
    }
  }

  // ── Datos de prueba ───────────────────────────────────────────────────────────
  const cargarDatosPrueba = async () => {
    setCargasCargando(true)
    try {
      const res = await cd2bPropuestaService.listarDatosPrueba(id)
      setCargas(res?.data?.cargas || [])
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudieron leer las cargas'))
    } finally {
      setCargasCargando(false)
    }
  }

  /**
   * Accesos de los puestos que se sembraron. Es lo que el asesor le entrega al prospecto
   * para que pruebe cada rol («entra como cajero y verás que NO ve los costos»).
   * Los que salen sin PIN no cupieron en los asientos del plan: se dicen igual, porque esa
   * es la explicación de por qué la demo tiene 3 accesos y no 11.
   */
  const verCredenciales = async () => {
    try {
      const res = await cd2bPropuestaService.credencialesDemo(id)
      const lista = res?.data?.credenciales || []
      setCredenciales(lista)
      if (lista.length === 0) toast('La empresa demo todavía no tiene usuarios sembrados.')
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudieron leer los accesos de la demo'))
    }
  }

  const giroSel = (catalogo.giros || []).find((g) => g.id === form.giro) || null

  /**
   * Lo que el rubro suele llevar contra lo que el paquete ya tiene.
   *
   * 🔴 Elegir el giro NO cambia el paquete, y la empresa nace con las áreas que el paquete
   * COTIZADO concede, no con las del rubro: una panadería cotizada sin Reparto o sin Inventario
   * avanzado nace sin «Producción del día», sin camionetas y sin entregas, y nadie recibe un
   * error. Por eso el rubro se OFRECE aquí con lo que falta y su precio lo calcula el motor de
   * siempre al añadirlo; nada se cobra hasta que el asesor guarda.
   */
  const sugeridosDelGiro = useMemo(() => {
    if (!giroSel) return null
    const codigos = giroSel.modulos || []
    const modulos = (catalogo.modulos || []).filter((m) => codigos.includes(m.code))
    const app = (catalogo.addons?.acceso || []).find((a) => codigos.includes(a.code)) || null
    const permiteExtras = (catalogo.ediciones || []).find((e) => e.edicion === form.edicion)?.permiteExtrasPago !== false
    const faltanModulos = modulos.filter((m) => !form.modulosSel.includes(m.code))
    const faltaApp = !!app && permiteExtras && !form.cargosAdicionales.some((x) => x.code === app.code)
    // Asientos: los puestos del rubro necesitan credencial. `form.seats` es el TOTAL del paquete
    // (arranca en lo que incluye la edición), así que solo se sube; nunca se le quita nada al asesor.
    const pide = giroSel.requisitos?.asientos || {}
    const hay = form.seats || {}
    const asientos = permiteExtras
      ? { completo: Math.max(Number(pide.completo) || 0, Number(hay.completo) || 0), operativo: Math.max(Number(pide.operativo) || 0, Number(hay.operativo) || 0) }
      : null
    const faltaAsientos = !!asientos && (asientos.completo > (Number(hay.completo) || 0) || asientos.operativo > (Number(hay.operativo) || 0))
    return { modulos, app, faltanModulos, faltaApp, asientos, faltaAsientos, pide, puestos: (giroSel.perfiles || []).map((p) => p.nombre).filter(Boolean) }
  }, [giroSel, catalogo, form.edicion, form.modulosSel, form.cargosAdicionales, form.seats])

  /** Marca los módulos y la app móvil que el rubro recomienda y el paquete no tiene. Guardar sigue siendo explícito. */
  const aplicarSugeridosDelGiro = () => {
    if (!sugeridosDelGiro) return
    sumarModulos(sugeridosDelGiro.faltanModulos.map((m) => m.code))
    if (sugeridosDelGiro.faltaApp) toggleAddonCargo(sugeridosDelGiro.app)
    if (sugeridosDelGiro.faltaAsientos) setForm((f) => ({ ...f, seats: sugeridosDelGiro.asientos }))
    toast.success('Lo que el rubro recomienda se añadió al paquete. Revisa el total y guarda la cotización.')
  }

  /**
   * Chequeo previo. Se interpone entre el botón y la siembra porque las brechas se pueden
   * saber ANTES, y una vez sembrada la demo el ajuste ya cuesta una addenda.
   *
   * Si no hay ninguna brecha no se abre nada: obligar a confirmar una hoja sin decisiones
   * dentro solo añade un clic.
   */
  const revisarAntesDeSembrar = async () => {
    if (!form.giro) return true
    try {
      const res = await cd2bPropuestaService.chequeoSiembra(id, form.giro)
      const c = res?.data || null
      if (!c || c.listo) {
        setChequeoSiembra(null)
        return true
      }
      setChequeoSiembra(c)
      return false
    } catch (e) {
      // Que el chequeo no responda no puede impedir sembrar: es una ayuda, no una puerta.
      toast.error(mensajeDeError(e, 'No se pudo revisar el paquete; se sigue sin chequeo'))
      return true
    }
  }

  /**
   * ¿La empresa demo ya tiene operación dentro? Se pregunta ANTES de sembrar.
   *
   * El motivo es que sembrar encima nunca sale del todo bien: el seeder es idempotente y
   * reusa lo que encuentra, así que los movimientos de compra de un producto que ya existía
   * no quedan marcados y después NO se pueden limpiar. El servidor ya avisaba de eso, pero
   * mandaba a «sembrar sobre una empresa sin catálogo» sin dar forma de llegar ahí.
   *
   * Y hay un segundo motivo, el que más se da: los datos que hay pueden ser de una siembra
   * vieja o de pruebas del propio asesor, y no ser de la calidad que se le quiere enseñar al
   * prospecto. Empezar de cero es lo normal, no la excepción.
   */
  const revisarSiHayDatos = async () => {
    try {
      const res = await cd2bPropuestaService.vaciarDatosDemo(id, { previsualizar: true })
      const d = res?.data || null
      if (!d || !(d.total > 0)) {
        setVaciado(null)
        return true
      }
      setVaciado(d)
      return false
    } catch (e) {
      // Una empresa ya en producción responde 409: ahí no se vacía nada, pero tampoco se
      // impide sembrar (el aviso de limpieza parcial del servidor sigue estando).
      if (e?.response?.status !== 409) {
        toast.error(mensajeDeError(e, 'No se pudo revisar si la empresa ya tiene datos'))
      }
      return true
    }
  }

  /**
   * Sigue una siembra en segundo plano hasta que termine. Devuelve su resultado, o `null`
   * si falló (en cuyo caso ya avisó). El intervalo es de 5 s: la siembra tarda minutos, así
   * que preguntar más a menudo solo añade ruido.
   */
  const seguirSiembra = async (cargaId) => {
    const INTERVALO_MS = 5000
    // 🔴 ESTE TECHO YA SE QUEDÓ CORTO UNA VEZ. Se puso en 20 min cuando el giro más pesado
    // medido era cafetería (~8 min); en sep-2026 restaurante estrenó motor de operación
    // histórica y su siembra completa se midió en 1209 s —20,1 min— sobre SQLite en
    // memoria. Contra Postgres, con un viaje de red por sentencia, es peor. O sea que el
    // asesor veía el aviso de "tardando demasiado" sobre una siembra que iba a terminar
    // bien. Se deja holgura de sobra: agotarlo no cancela nada (ver abajo), así que un
    // techo generoso no cuesta, y uno corto miente.
    const LIMITE_MS = 45 * 60 * 1000
    const hasta = Date.now() + LIMITE_MS
    while (Date.now() < hasta) {
      await new Promise((r) => setTimeout(r, INTERVALO_MS))
      try {
        const res = await cd2bPropuestaService.estadoSiembra(id, cargaId)
        const d = res?.data || {}
        if (d.estado === 'listo') return d
        if (d.estado === 'error') {
          toast.error(d.error || 'La siembra falló')
          // Aunque falle a medias, lo escrito queda MARCADO en su carga y se puede borrar.
          cargarDatosPrueba()
          return null
        }
      } catch (e) {
        // 404 = el nube-server se reinició y perdió el seguimiento. El trabajo no se pierde:
        // la carga sigue en el tenant. Se dice y se remite a la lista de cargas.
        if (e?.response?.status === 404) {
          toast.error('Se perdió el seguimiento de la siembra. Revisa las cargas del tenant.')
          cargarDatosPrueba()
          return null
        }
      }
    }
    // NO es un fallo: dejar de MIRAR no es dejar de sembrar. El trabajo vive en el
    // servidor y termina igual. Decir "falló" haría que el asesor borre la carga y
    // vuelva a empezar un trabajo que estaba a punto de salir bien.
    toast('Dejamos de seguir la siembra, pero sigue corriendo en el servidor. Mira «Ver cargas» en un rato.')
    cargarDatosPrueba()
    return null
  }

  const sembrarDatos = async ({ omitirChequeo = false, omitirVaciado = false } = {}) => {
    // Sin giro no hay plantilla que sembrar: el servidor responde SIN_GIRO y la cotización
    // ya está guardada. Se corta antes, con el motivo a la vista (el botón está deshabilitado).
    if (!form.giro) {
      toast.error('Elige el giro del negocio antes de sembrar')
      return
    }
    if (!omitirChequeo && !(await revisarAntesDeSembrar())) return
    // Lo último antes de escribir: si la empresa ya tiene datos, se pregunta qué hacer con
    // ellos. Va después del chequeo de paquete para no encadenar dos preguntas seguidas.
    if (!omitirVaciado && !(await revisarSiHayDatos())) return
    const esOperacion = alcanceSiembra === 'operacion'
    const seguir = await confirmar({
      titulo: esOperacion ? 'Sembrar la empresa demo completa' : 'Sembrar el catálogo de ejemplo',
      descripcion: esOperacion
        ? `Se sembrará la empresa demo completa del giro «${giroSel?.label || form.giro}» con ~12 meses de operación. ` +
          'Tarda varios minutos y se puede revertir después.'
        : 'Se sembrará solo el catálogo de ejemplo del giro. Se puede revertir después.',
      textoConfirmar: 'Sembrar',
    })
    if (!seguir) return
    setSembrando(true)
    try {
      const res = await cd2bPropuestaService.sembrarDatosPrueba(id, {
        alcance: alcanceSiembra,
        giro: form.giro,
      })
      let d = res?.data || {}
      if (d.advertencia) toast.error(d.advertencia)
      // La operación de 12 meses responde `estado: 'en_curso'` y sigue en el servidor: no
      // cabe en una petición HTTP (medido: ferretería ~1,5 min, cafetería ~8, restaurante
      // ~20). Se sigue su avance hasta que termine, sin dejar la pantalla bloqueada.
      if (d.estado === 'en_curso' && d.cargaId) {
        toast.success('Siembra en marcha. Puede tardar hasta media hora; puedes seguir trabajando.')
        d = await seguirSiembra(d.cargaId)
        if (!d) return
      }
      toast.success(`Datos sembrados: ${d.filas ?? 0} fila(s)`)
      // Lo que el seeder NO pudo sembrar se dice AQUÍ, no se descubre el día de la entrega.
      for (const b of d.bloquesOmitidos || []) toast.error(`No sembrado — ${b.bloque}: ${b.motivo}`)
      if (d.asientos?.sinCredencial > 0) {
        toast(
          `${d.asientos.sinCredencial} usuario(s) quedaron sin credencial: no caben en los ` +
            `asientos del plan (${d.asientos.cupo?.completo ?? '—'} completos, ${d.asientos.cupo?.operativo ?? '—'} operativos).`,
        )
      }
      cargarDatosPrueba()
      // Los accesos recién sembrados, sin tener que pedirlos: es lo primero que el asesor
      // necesita cuando la siembra acaba (son lo que le pasa al prospecto).
      verCredenciales()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudieron sembrar los datos'))
    } finally {
      setSembrando(false)
    }
  }

  const limpiarYSembrar = async () => {
    const total = vaciado?.total ?? 0
    const seguir = await confirmar({
      titulo: 'Vaciar y sembrar de nuevo',
      descripcion:
        `Se eliminarán ${total} fila(s) de la empresa de prueba: catálogo, clientes, ventas, ` +
        'inventario y contabilidad. La configuración, el emisor y los usuarios se conservan.',
      textoConfirmar: 'Vaciar y sembrar',
      variante: 'destructiva',
    })
    if (!seguir) return
    try {
      const res = await cd2bPropuestaService.vaciarDatosDemo(id)
      toast.success(`Empresa vaciada: ${res?.data?.total ?? 0} fila(s) eliminadas`)
      setVaciado(null)
      setCredenciales(null)
      cargarDatosPrueba()
      // El chequeo de paquete ya se hizo (o se hará): sembrar directo evita repetirlo.
      await sembrarDatos({ omitirChequeo: true, omitirVaciado: true })
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo vaciar la empresa'))
    }
  }

  /**
   * Borrar lo sembrado para volver a empezar. Dos pasos SIEMPRE: primero se cuenta sin
   * borrar (`previsualizar`) y se enseña el desglose, luego se confirma. Nunca "todo o
   * nada" a ciegas sobre el tenant de un cliente.
   */
  const previsualizarReversion = async (carga) => {
    try {
      const res = await cd2bPropuestaService.revertirDatosPrueba(id, carga.id, { previsualizar: true })
      setReversion({ carga, previa: res?.data || null })
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo calcular qué se eliminaría'))
    }
  }

  // ── Puesta en producción ──────────────────────────────────────────────────────
  const revisarProduccion = async ({ silencioso = false } = {}) => {
    try {
      const res = await cd2bPropuestaService.listoParaProduccion(id)
      setChequeo(res?.data || null)
      if (!silencioso && res?.data?.listo) toast.success('Todo listo para arrancar')
    } catch (e) {
      if (!silencioso) toast.error(mensajeDeError(e, 'No se pudo revisar'))
    }
  }

  const confirmarReversion = async () => {
    if (!reversion) return
    try {
      const res = await cd2bPropuestaService.revertirDatosPrueba(id, reversion.carga.id)
      toast.success(`Carga revertida: ${res?.data?.filas ?? 0} fila(s) eliminadas`)
      setReversion(null)
      // La lista de accesos que se acaba de ver ya no describe al tenant: se retira en vez
      // de dejar en pantalla PINs de usuarios que quizá acaban de desaparecer.
      setCredenciales(null)
      cargarDatosPrueba()
      revisarProduccion({ silencioso: true })
    } catch (e) {
      // Un maestro que el negocio YA usó no se borra: no es un fallo del servidor, es una
      // decisión de negocio y llega con su motivo. Se muestra tal cual.
      toast.error(mensajeDeError(e, 'No se pudo revertir la carga'))
    }
  }

  const pasarAProduccion = async () => {
    const avisos = (chequeo?.advertencias || []).map((a) => `• ${a.mensaje}`).join('\n')
    const seguir = await confirmar({
      titulo: 'Pasar a producción',
      descripcion:
        `${form.empresaNombre} pasará a producción: la empresa de prueba se convierte en la real.` +
        (avisos ? `\n\nAntes de seguir:\n${avisos}` : ''),
      textoConfirmar: 'Pasar a producción',
    })
    if (!seguir) return
    try {
      await cd2bPropuestaService.pasarAProduccion(id)
      toast.success(`${form.empresaNombre} ya está en producción`)
      cargar()
    } catch (e) {
      const d = e?.response?.data
      if (d?.code === 'DATOS_PRUEBA_SIN_DECIDIR') {
        toast.error('Falta decidir qué hacer con los datos de prueba (tarjeta «Datos de prueba»).', { duration: 10000 })
        cargarDatosPrueba()
        return
      }
      // El backend rechazó por requisitos: se repinta la lista de verificación con lo que él
      // vio, no con lo que el asesor tuviera en pantalla de hace diez minutos.
      if (d?.code === 'REQUISITOS_SIN_CUMPLIR') {
        setChequeo({ listo: false, bloqueos: d.bloqueos || [], advertencias: [] })
        toast.error('Faltan requisitos para arrancar: revisá los puntos en rojo.', { duration: 10000 })
        return
      }
      if (d?.code === 'EMISOR_NO_APLICADO') {
        toast.error(mensajeDeError(e), { duration: 12000 })
        return
      }
      toast.error(mensajeDeError(e, 'No se pudo pasar a producción'))
    }
  }

  // Solicita un plugin/desarrollo a medida al equipo CoreDesk para esta propuesta.
  const solicitarPlugin = async () => {
    const datos = await pedirDatos({
      titulo: 'Solicitar plugin a medida',
      descripcion: 'La solicitud le llega al equipo CoreDesk ligada a esta cotización.',
      campos: [
        { clave: 'titulo', etiqueta: 'Título del plugin o desarrollo' },
        { clave: 'descripcion', etiqueta: 'Qué debe hacer', multilinea: true },
      ],
      textoConfirmar: 'Enviar solicitud',
    })
    if (!datos) return
    try {
      await cd2bPropuestaService.solicitarPlugin(id, { titulo: datos.titulo.trim(), descripcion: datos.descripcion.trim() })
      toast.success('Solicitud de plugin enviada al equipo CoreDesk')
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo enviar la solicitud'))
    }
  }

  // ── Derivados ─────────────────────────────────────────────────────────────────
  const esNube = form.modalidad === 'nube'
  const tieneDemo = !!propuesta?.demo?.tenantId
  // 🔴 Y EL CATÁLOGO DEL CONTADOR, SI LO HAY. Cuando el cliente declara que su contador tiene
  // catálogo propio, la empresa no puede crearse hasta que el sistema lo ENTIENDA: un código
  // igual al nuestro no significa la misma cuenta, y medido con un catálogo real 54 de los 72
  // que coincidían significaban otra cosa. La empresa arrancaría con el Estado de Resultados
  // invertido y en silencio.
  //
  // Esto es solo la cortesía de no dejar pulsar un botón que va a fallar: el freno de verdad
  // está en el api (`exigirCatalogoEntendido`), porque un freno que vive en la UI no es un freno.
  //
  // Y frena TAMBIÉN cuando nadie ha contestado la pregunta. El silencio se resolvía como «usa
  // el estándar», que acierta el 90 % de las veces —lo justo para que nadie lo note— y el 10 %
  // restante es una empresa contabilizando contra un plan que su contador nunca vio.
  /**
   * 🔴 LO QUE IMPIDE CREAR EL BORRADOR, o cadena vacía si no hay nada que impida.
   *
   * Solo aplica a la cotización nueva. La pregunta del catálogo decide con qué plan de cuentas
   * nace la empresa, y dejarla para después es cómo se llegaba a empresas contabilizando contra
   * un plan que su contador nunca vio: se contestaba tarde, o no se contestaba. Con «no» basta
   * la respuesta; con «sí» hace falta además su archivo, porque una cotización que declara
   * catálogo propio y no lo trae es justo el estado que no sirve para nada — y el archivo lo
   * tiene delante ahora.
   */
  //
  // Y solo en modalidad NUBE (B1): es la única en la que la empresa se crea desde aquí y el
  // catálogo viaja con ella. En local/vps se trae desde el escritorio.
  const frenoDeCreacion = esNueva
    ? frenoCatalogoEnCreacion({ modalidad: form.modalidad, declaracion: declaracionCatalogo })
    : ''

  // Mismo criterio para la cotización ya creada: fuera de la nube no hay empresa demo que frenar.
  const { sinDeclarar: catalogoSinDeclarar, frena: catalogoFrena } = estadoFrenoCatalogo({
    modalidad: form.modalidad,
    estado: catalogoContador,
  })
  const entregaCatalogo = modalidadEntregaCatalogo(form.modalidad)
  // País GUARDADO en la cotización: la demo se crea con el del servidor, no con lo que hay en el
  // formulario sin guardar. Con la empresa ya creada el país queda de solo lectura (el servidor
  // rechaza el cambio: desalinearía sub, tenant y catálogo contable).
  const paisGuardado = propuesta?.pais || ''
  // Excepción: cotización PAGADA que nunca capturó el país (anterior al país obligatorio) y sin
  // tenant todavía. Sin catálogo sembrado no hay nada que desalinear, y sin país no se puede
  // entregar: se deja fijar UNA vez (el servidor aplica la misma regla).
  const tieneTenant = !!(propuesta?.demo?.tenantId || propuesta?.instanciaNube?.tenantId)
  const empresaCreada = !!(propuesta?.demo?.subscriptionId || propuesta?.subscriptionId)
  const paisBloqueado = empresaCreada && (!!paisGuardado || tieneTenant || propuesta?.estado !== 'pagada')
  const esPagada = propuesta?.estado === 'pagada'
  const paisSinGuardar = !!form.pais && form.pais !== paisGuardado
  const paisFrena = !paisGuardado
  // Pagada sin demo se entrega por el camino B: una demo ahora dejaría dos suscripciones.
  const puedeCrearDemo = esNube && !['pagada', 'activada', 'aplicada', 'cancelada'].includes(propuesta?.estado) && !tieneDemo && !catalogoFrena && !paisFrena
  // Alcance resuelto del paquete marcado (réplica del cálculo de la autoridad).
  const alcance = useMemo(
    () => resolverAlcance({ edicion: form.edicion, modulosSel: form.modulosSel, catalogo }),
    [form.edicion, form.modulosSel, catalogo],
  )
  const seatsBase = catalogo.seatsPorEdicion?.[form.edicion] || {}
  const edicionLabel = (ed) => (catalogo.ediciones || []).find((e) => e.edicion === ed)?.label || ed
  // Impuesto del país elegido, como lo publica la API (sin tasa escrita en el portal).
  const impuesto = impuestoDelPais((paises || []).find((x) => x.code === form.pais)?.impuesto)

  // El gerente de país abre las cotizaciones de su equipo SOLO PARA LEER (docs/179): la API ya
  // rechaza cualquier cambio; aquí se desactivan los controles para que no lo intente.
  const soloLectura = !!propuesta?.soloLectura

  return {
    id, esNueva, loading, saving, catalogo, propuesta, form, setForm,
    // Catálogos y países
    paises, paisesError, catFiscal, catFiscalCargando, impuesto,
    // Precio y comisión
    quote, comision, previewOpen, setPreviewOpen, previewData, menuPrevio,
    // Paquete
    toggleModulo, cargoActivo, toggleAddonCargo, modulosDeEdicion, elegirGiro, cambiarEdicion, sumarModulos,
    appMovilVendida, alcance, seatsBase, giroSel, edicionLabel, sugeridosDelGiro, aplicarSugeridosDelGiro,
    // Guardar / enviar
    guardar, enviando, enviarAlDueno, frenoDeCreacion,
    // Catálogo del contador
    setCatalogoContador, setDeclaracionCatalogo, catalogoSinDeclarar, catalogoFrena, entregaCatalogo,
    // País
    paisBloqueado, paisSinGuardar, paisFrena, esPagada,
    // Demo
    esNube, tieneDemo, puedeCrearDemo, demoInfo, crearDemo, verAcceso, extenderDemo, solicitarPlugin,
    // Datos de prueba
    cargas, cargasCargando, cargarDatosPrueba, alcanceSiembra, setAlcanceSiembra, sembrando, sembrarDatos,
    credenciales, verCredenciales, chequeoSiembra, setChequeoSiembra, vaciado, setVaciado, limpiarYSembrar,
    reversion, setReversion, previsualizarReversion, confirmarReversion,
    // Producción
    chequeo, revisarProduccion, pasarAProduccion,
    cargar, recargarPropuesta, soloLectura, dialogo,
  }
}

export default useCotizacion
